import { enqueueChatMessage } from '@platform/chat';
import { chatMessageEntrypoint } from '@platform/chat/messageEntrypoint';
import { selectActiveConvId, updateEntity } from '@platform/timeline';
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Icon,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@shared/ui';
import { deepMerge } from '@shared/utils/merge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerWidgetRenderer } from '../registry';
import type {
  DeepPartial,
  MultipleChoiceEntity,
  MultipleChoiceOption,
  MultipleChoiceWidgetStyles,
  TimelineWidgetProps,
} from '../types';

export interface MultipleChoiceWidgetProps extends TimelineWidgetProps<MultipleChoiceEntity> {
  debugMode?: boolean;
  multipleChoiceWidgetStyles?: DeepPartial<MultipleChoiceWidgetStyles>;
}

const MULTISELECT_COUNTDOWN_SECONDS = 20;

// Register renderer + visibility
registerWidgetRenderer(
  'multiple_choice',
  ({ entity, debugMode, multipleChoiceWidgetStyles }) => (
    <MultipleChoiceWidget
      key={(entity as any).id}
      entity={entity as any}
      debugMode={debugMode}
      multipleChoiceWidgetStyles={multipleChoiceWidgetStyles}
    />
  ),
  { visibility: { normal: true, debug: true } }
);

export function MultipleChoiceWidget({
  entity,
  debugMode = false,
  multipleChoiceWidgetStyles,
}: MultipleChoiceWidgetProps) {
  const dispatch = useDispatch();
  const activeConvId = useSelector(selectActiveConvId);
  const convId = (activeConvId || (entity as any).convId || null) as string | null;

  const {
    question_id,
    question,
    instructions,
    options = [],
    allow_multiple,
    required,
    selected_option_ids,
    locked_by_user,
    answered_at,
    status,
    error,
  } = entity.props || {};

  const [localSelected, setLocalSelected] = useState<string[]>(() => selected_option_ids || []);
  const [isLocallyLocked, setIsLocallyLocked] = useState(false);
  const [hasSentOnce, setHasSentOnce] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const countdownRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const selectedRef = useRef<string[]>(localSelected);

  const clearTimers = useCallback(() => {
    setCountdown(null);
    countdownRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    selectedRef.current = localSelected;
  }, [localSelected]);

  // Sync local state when backend updates selection/lock
  useEffect(() => {
    if (Array.isArray(selected_option_ids)) {
      setLocalSelected(selected_option_ids);
    }
  }, [selected_option_ids]);

  useEffect(() => {
    if (locked_by_user) {
      setIsLocallyLocked(true);
      clearTimers();
    }
  }, [clearTimers, locked_by_user]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const isMulti = !!allow_multiple;
  const entityId = question_id || entity.id;
  const isError = status === 'error' || !!error;
  const isCompleted = !!answered_at && !isError;

  const disabled = !convId || isError || !!locked_by_user || isLocallyLocked || hasSentOnce;

  const selectedOptions: MultipleChoiceOption[] = useMemo(
    () =>
      (options as MultipleChoiceOption[]).filter((opt) => localSelected.includes(opt.option_id)),
    [options, localSelected]
  );

  function startCountdown() {
    if (!isMulti) return;
    if (countdownRef.current !== null) return; // already running
    countdownRef.current = MULTISELECT_COUNTDOWN_SECONDS;
    setCountdown(MULTISELECT_COUNTDOWN_SECONDS);

    if (typeof window === 'undefined') return;

    intervalRef.current = window.setInterval(() => {
      if (countdownRef.current === null) {
        return;
      }
      const next = countdownRef.current - 1;
      countdownRef.current = next;
      if (next <= 0) {
        clearTimers();
        // Auto-send if we still have a selection and haven't sent yet
        if (
          selectedRef.current.length > 0 &&
          !hasSentOnce &&
          !isError &&
          !isLocallyLocked &&
          !locked_by_user
        ) {
          doSendSelection();
        }
        return;
      }
      setCountdown(next);
    }, 1000);
  }

  function doSendSelection() {
    if (!convId) return;
    if (disabled) return;

    const selectedIds = selectedRef.current;
    if (!selectedIds || selectedIds.length === 0) return;

    // Let the backend preserve the existing conversation profile by omitting an explicit profile.
    const profile = '';
    const now = Date.now();

    dispatch(
      updateEntity({
        convId,
        entityId,
        props: {
          selected_option_ids: selectedIds,
          locked_by_user: true,
          answered_at: now,
        },
      }) as any
    );

    // Skip sending message to LLM for transcript selection questions
    // (the page will handle navigation directly)
    const isTranscriptSelection =
      question_id === 'select_transcript' || question_id === 'transcript_selection';

    if (isTranscriptSelection) {
      setIsLocallyLocked(true);
      setHasSentOnce(true);
      clearTimers();
      return;
    }

    const labelList = selectedOptions.map((o) => o.label).filter(Boolean);
    const labelText = labelList.length > 0 ? labelList.join(', ') : selectedIds.join(', ');
    const idsText = JSON.stringify(selectedIds);
    const qid = question_id || entity.id;
    const questionText = question || 'the question';

    const text =
      isMulti && selectedIds.length > 1
        ? `I choose: ${labelText} (option_ids: ${idsText}, question_id: ${qid}) for "${questionText}".`
        : `I choose: "${labelText}" (option_id: ${selectedIds[0] || ''}, question_id: ${qid}) for "${questionText}".`;

    dispatch(
      enqueueChatMessage({
        convId,
        profile,
        text,
        messageEntrypoint: chatMessageEntrypoint.widgetAction,
      }) as any
    );

    setIsLocallyLocked(true);
    setHasSentOnce(true);
    clearTimers();
  }

  function handleToggle(optionId: string) {
    if (disabled) return;
    const current = selectedRef.current;

    if (isMulti) {
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      setLocalSelected(next);
      selectedRef.current = next;

      if (next.length === 0) {
        clearTimers();
      } else if (countdownRef.current === null) {
        startCountdown();
      }
      return;
    }

    // Single-choice: send immediately
    if (current.includes(optionId)) {
      // No-op: radio stays selected; we keep the lock
      return;
    }
    setLocalSelected([optionId]);
    selectedRef.current = [optionId];
    doSendSelection();
  }

  // Restore original colors for status
  const statusColor = isError ? 'error' : isCompleted ? 'success' : 'primary';

  // Default styles if not provided
  const defaultStyles: MultipleChoiceWidgetStyles = {
    active: {
      backgroundColor: '', // Will use className bg-gradient-to-r from-sage-50 to-sky-50
      borderColor: '',
      textColor: '', // Uses default text color
      chipBorderColor: '',
      chipTextColor: '',
    },
    completed: {
      backgroundColor: '', // Will use className bg-gradient-to-r from-sage-50 to-sky-50
      borderColor: '',
      textColor: '',
      chipBorderColor: '',
      chipTextColor: '',
    },
    error: {
      backgroundColor: '', // Will use className bg-gradient-to-r from-sage-50 to-sky-50
      borderColor: '',
      textColor: '',
      chipBorderColor: '',
      chipTextColor: '',
      errorBgColor: 'rgba(254, 242, 242, 1)', // bg-red-50
      errorBorderColor: 'rgba(254, 202, 202, 1)', // border-red-200
      errorTextColor: 'error.main', // color="error"
    },
  };

  const styles = deepMerge(defaultStyles, multipleChoiceWidgetStyles);
  const stateStyles = isError ? styles.error : isCompleted ? styles.completed : styles.active;

  return (
    <Paper
      variant="outlined"
      className={`p-4 w-full min-w-0 ${!stateStyles.backgroundColor ? 'bg-gradient-to-r from-sage-50 to-sky-50' : ''}`}
      sx={{
        bgcolor: stateStyles.backgroundColor || undefined,
        borderColor: stateStyles.borderColor || undefined,
        color: stateStyles.textColor || undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: stateStyles.textColor || `${statusColor}.main` }}>
          {isError ? 'error_outline' : isCompleted ? 'task_alt' : 'quiz'}
        </Icon>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: stateStyles.textColor || undefined }}
            >
              Multiple Choice Question
            </Typography>
            {required ? (
              <Chip
                size="small"
                variant="outlined"
                label="Required"
                color={!stateStyles.chipTextColor ? 'error' : undefined}
                customColor={stateStyles.chipTextColor}
                customBorderColor={stateStyles.chipBorderColor}
              />
            ) : null}
            {isMulti ? (
              <Chip
                size="small"
                variant="outlined"
                label="Multiple select"
                color={!stateStyles.chipTextColor ? 'primary' : undefined}
                customColor={stateStyles.chipTextColor}
                customBorderColor={stateStyles.chipBorderColor}
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label="Single select"
                color={!stateStyles.chipTextColor ? 'primary' : undefined}
                customColor={stateStyles.chipTextColor}
                customBorderColor={stateStyles.chipBorderColor}
              />
            )}
            {isCompleted && !isError ? (
              <Chip
                size="small"
                variant="outlined"
                label="Answered"
                color={!styles.completed.chipTextColor ? 'success' : undefined}
                customColor={styles.completed.chipTextColor}
                customBorderColor={styles.completed.chipBorderColor}
              />
            ) : null}
            {isError ? (
              <Chip
                size="small"
                variant="outlined"
                label="Error"
                color={!styles.error.chipTextColor ? 'error' : undefined}
                customColor={styles.error.chipTextColor}
                customBorderColor={styles.error.chipBorderColor}
              />
            ) : null}
          </div>

          {question ? (
            <Typography
              variant="body1"
              className="mb-1 font-medium"
              sx={{ color: stateStyles.textColor || undefined }}
            >
              {question}
            </Typography>
          ) : null}

          {instructions ? (
            <Typography
              variant="body2"
              className="mb-3"
              color={stateStyles.textColor ? undefined : 'text.secondary'}
              sx={{ color: stateStyles.textColor || undefined }}
            >
              {instructions}
            </Typography>
          ) : null}

          {options.length === 0 ? (
            <Typography
              variant="body2"
              color={stateStyles.textColor ? undefined : 'text.secondary'}
              sx={{ color: stateStyles.textColor || undefined }}
            >
              No options available for this question.
            </Typography>
          ) : isMulti ? (
            <FormGroup>
              {(options as MultipleChoiceOption[]).map((opt) => (
                <div key={opt.option_id} className="rounded-lg px-3 py-1 mb-1 flex items-center">
                  <FormControlLabel
                    labelPlacement="start"
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      margin: 0,
                    }}
                    control={
                      <Checkbox
                        checked={localSelected.includes(opt.option_id)}
                        onChange={() => handleToggle(opt.option_id)}
                        disabled={disabled}
                        sx={{
                          color: stateStyles.textColor || undefined,
                          '&.Mui-checked': {
                            color: stateStyles.textColor || undefined,
                          },
                          '&.Mui-disabled': {
                            color: stateStyles.textColor || undefined,
                            opacity: 0.5,
                          },
                        }}
                      />
                    }
                    label={
                      <div className="flex flex-col">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ color: stateStyles.textColor || undefined }}
                        >
                          {opt.label}
                        </Typography>
                        {opt.description ? (
                          <Typography
                            variant="caption"
                            color={stateStyles.textColor ? undefined : 'text.secondary'}
                            sx={{ color: stateStyles.textColor || undefined }}
                          >
                            {opt.description}
                          </Typography>
                        ) : null}
                      </div>
                    }
                  />
                </div>
              ))}
            </FormGroup>
          ) : (
            <RadioGroup
              value={localSelected[0] || ''}
              onChange={(e) => handleToggle((e.target as HTMLInputElement).value)}
            >
              {(options as MultipleChoiceOption[]).map((opt) => (
                <FormControlLabel
                  key={opt.option_id}
                  value={opt.option_id}
                  labelPlacement="start"
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    margin: 0,
                  }}
                  control={
                    <Radio
                      disabled={disabled}
                      sx={{
                        color: stateStyles.textColor || undefined,
                        '&.Mui-checked': {
                          color: stateStyles.textColor || undefined,
                        },
                        '&.Mui-disabled': {
                          color: stateStyles.textColor || undefined,
                          opacity: 0.5,
                        },
                      }}
                    />
                  }
                  label={
                    <div className="flex flex-col">
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: stateStyles.textColor || undefined }}
                      >
                        {opt.label}
                      </Typography>
                      {opt.description ? (
                        <Typography
                          variant="caption"
                          color={stateStyles.textColor ? undefined : 'text.secondary'}
                          sx={{ color: stateStyles.textColor || undefined }}
                        >
                          {opt.description}
                        </Typography>
                      ) : null}
                    </div>
                  }
                />
              ))}
            </RadioGroup>
          )}

          {/* Selection summary + actions */}
          {localSelected.length > 0 ? (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Typography
                variant="caption"
                color={stateStyles.textColor ? undefined : 'text.secondary'}
                sx={{ color: stateStyles.textColor || undefined }}
              >
                Selected:
              </Typography>
              {selectedOptions.length > 0 ? (
                selectedOptions.map((opt) => (
                  <Chip
                    key={opt.option_id}
                    size="small"
                    label={opt.label}
                    variant="outlined"
                    color={!stateStyles.chipTextColor ? 'primary' : undefined}
                    customColor={stateStyles.chipTextColor}
                    customBorderColor={stateStyles.chipBorderColor}
                  />
                ))
              ) : (
                <Typography
                  variant="caption"
                  color={stateStyles.textColor ? undefined : 'text.secondary'}
                  sx={{ color: stateStyles.textColor || undefined }}
                >
                  {localSelected.join(', ')}
                </Typography>
              )}
            </div>
          ) : null}

          {isMulti && localSelected.length > 0 && !hasSentOnce && !isError && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <Button
                variant="contained"
                size="small"
                onClick={doSendSelection}
                disabled={disabled}
                startIcon={<Icon>send</Icon>}
              >
                Send selection
              </Button>
              {countdown !== null ? (
                <div className="flex items-center gap-2">
                  <Typography
                    variant="caption"
                    color={stateStyles.textColor ? undefined : 'text.secondary'}
                    sx={{ color: stateStyles.textColor || undefined }}
                  >
                    Auto-sending in {countdown}s…
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      ((MULTISELECT_COUNTDOWN_SECONDS - countdown) /
                        MULTISELECT_COUNTDOWN_SECONDS) *
                      100
                    }
                    style={{ width: 120 }}
                  />
                  <Button size="small" variant="text" onClick={clearTimers} disabled={disabled}>
                    Cancel timer
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {isError && (
            <div className="mt-3">
              <Paper
                className={
                  !stateStyles.errorBgColor ? 'p-2 bg-red-50 border border-red-200' : 'p-2'
                }
                sx={{
                  bgcolor: stateStyles.errorBgColor || undefined,
                  border: stateStyles.errorBorderColor
                    ? `1px solid ${stateStyles.errorBorderColor}`
                    : undefined,
                }}
              >
                <Typography
                  variant="body2"
                  color={!stateStyles.errorTextColor ? 'error' : undefined}
                  sx={{ color: stateStyles.errorTextColor || stateStyles.textColor || undefined }}
                >
                  {error || 'An error occurred while processing this question.'}
                </Typography>
              </Paper>
            </div>
          )}

          {isCompleted && !isError && answered_at ? (
            <Typography
              variant="caption"
              color={stateStyles.textColor ? undefined : 'text.secondary'}
              sx={{ color: stateStyles.textColor || undefined }}
              className="mt-2 block"
            >
              Answered at {new Date(answered_at).toLocaleTimeString()}
            </Typography>
          ) : null}

          {debugMode && (
            <details className="mt-3 bg-gray-100 rounded p-2">
              <summary className="cursor-pointer text-xs font-mono">
                Debug — MultipleChoiceEntity
              </summary>
              <pre className="text-xs mt-2 overflow-auto max-h-64 whitespace-pre">
                {JSON.stringify(entity, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </Paper>
  );
}
